import {useEffect, useRef, useState} from "react";
import {
    comparePaths,
    ensureBrickToolbarHost,
    getHeadingConfig,
    getShouldNotGroupWhenFull,
    getToolbarItems,
    propagateFontSizeToListItems
} from "./brick-utils";

export default function useRichTextCkEditorBridge({
    bricks,
    disabled,
    enableBricks,
    inputRef,
    editorHostRef,
    toolBar,
    toolBarSet,
    value,
    showBrickHandle,
    handleInsertPanelKeyDown,
    capturePendingSlashCommand,
    syncSlashMenu,
    handleChipDoubleClick,
    handleSelectedBrickEnter,
    handleEditHandleClick,
    handleVelocityBrickClick,
    setToolbarHostNode
}) {
    const [loading, setLoading] = useState(true);
    const editorRef = useRef();
    const editableElementRef = useRef(null);
    const enableBricksRef = useRef(enableBricks);
    const handleInsertPanelKeyDownRef = useRef(handleInsertPanelKeyDown);
    const capturePendingSlashCommandRef = useRef(capturePendingSlashCommand);
    const syncSlashMenuRef = useRef(syncSlashMenu);
    const handleChipDoubleClickRef = useRef(handleChipDoubleClick);
    const handleSelectedBrickEnterRef = useRef(handleSelectedBrickEnter);
    const handleEditHandleClickRef = useRef(handleEditHandleClick);
    const handleVelocityBrickClickRef = useRef(handleVelocityBrickClick);
    const setToolbarHostNodeRef = useRef(setToolbarHostNode);

    useEffect(() => {
        enableBricksRef.current = enableBricks;
    }, [enableBricks]);

    useEffect(() => {
        handleInsertPanelKeyDownRef.current = handleInsertPanelKeyDown;
    }, [handleInsertPanelKeyDown]);

    useEffect(() => {
        capturePendingSlashCommandRef.current = capturePendingSlashCommand;
    }, [capturePendingSlashCommand]);

    useEffect(() => {
        syncSlashMenuRef.current = syncSlashMenu;
    }, [syncSlashMenu]);

    useEffect(() => {
        handleChipDoubleClickRef.current = handleChipDoubleClick;
    }, [handleChipDoubleClick]);

    useEffect(() => {
        handleSelectedBrickEnterRef.current = handleSelectedBrickEnter;
    }, [handleSelectedBrickEnter]);

    useEffect(() => {
        handleEditHandleClickRef.current = handleEditHandleClick;
    }, [handleEditHandleClick]);

    useEffect(() => {
        handleVelocityBrickClickRef.current = handleVelocityBrickClick;
    }, [handleVelocityBrickClick]);

    useEffect(() => {
        setToolbarHostNodeRef.current = setToolbarHostNode;
    }, [setToolbarHostNode]);

    useEffect(() => {
        if (!editorRef.current) return;

        if (disabled) {
            editorRef.current.enableReadOnlyMode('form-spec');
        } else {
            editorRef.current.disableReadOnlyMode('form-spec');
        }
    }, [disabled]);

    useEffect(() => {
        const toolbarItems = getToolbarItems(toolBarSet, toolBar);
        const managedToolbarCommandNames = Array.from(new Set(
            (bricks || []).flatMap((brick) => brick?.disabledToolbarCommands || [])
        ));
        let toolbarResolveTimer = null;
        let toolbarObserver = null;
        let selectionChangeHandler = null;
        let editableKeyDownHandler = null;
        let editableDoubleClickHandler = null;
        let editableClickHandler = null;

        let applyingExternalValue = false;
        const setDataExternally = (e) => {
            const nextValue = e.detail.data || '';
            applyingExternalValue = true;
            try { editorRef.current?.setData(nextValue); }
            finally { applyingExternalValue = false; }
        };

        const handleEditorReady = (editor) => {
            const syncDisabledToolbarCommands = () => {
                const selected = editor.model?.document?.selection?.getSelectedElement?.();
                const selectedBrickId = selected?.name === 'velocityBrick'
                    ? selected.getAttribute('brickId') || ''
                    : '';
                const selectedBrick = selectedBrickId
                    ? (bricks || []).find((brick) => brick?.id === selectedBrickId)
                    : null;
                const disabledCommands = new Set(selectedBrick?.disabledToolbarCommands || []);

                managedToolbarCommandNames.forEach((commandName) => {
                    const command = editor.commands.get(commandName);
                    if (!command) return;

                    if (disabledCommands.has(commandName)) {
                        command.forceDisabled?.('velocity-brick-selection');
                    } else {
                        command.clearForceDisabled?.('velocity-brick-selection');
                    }
                });
            };

            editor.setData(value || '');

            if (disabled) {
                editor.enableReadOnlyMode('form-spec');
            }

            editor.model.document.on('change:data', () => {
                const editableElement = editor.ui.getEditableElement();
                propagateFontSizeToListItems(editableElement);

                const rawHtml = editor.getData();
                const container = document.createElement('div');
                container.innerHTML = rawHtml;
                propagateFontSizeToListItems(container);
                const patchedHtml = container.innerHTML;

                const input = inputRef.current;
                const lastValue = input.value;
                input.value = patchedHtml;
                const evt = new Event('input', {bubbles: true});
                evt.changeOrigin = applyingExternalValue ? 'programmatic' : 'user';
                const tracker = input._valueTracker;
                if (tracker) tracker.setValue(lastValue);
                input.dispatchEvent(evt);

                syncSlashMenuRef.current?.();
            });

            const editableEl = editor.ui.getEditableElement();
            editableElementRef.current = editableEl;
            selectionChangeHandler = () => {
                syncDisabledToolbarCommands();
                syncSlashMenuRef.current?.();
            };
            editor.model.document.selection.on('change:range', selectionChangeHandler);
            syncDisabledToolbarCommands();

            if (editableEl) {
                editableKeyDownHandler = (event) => {
                    if (!enableBricksRef.current) return;

                    if (
                        event.key === 'Enter'
                        && !event.metaKey
                        && !event.ctrlKey
                        && !event.altKey
                        && !event.shiftKey
                    ) {
                        const selectedBrick = editor.model?.document?.selection?.getSelectedElement?.();
                        if (selectedBrick?.name === 'velocityBrick') {
                            const chip = editableEl.querySelector('.ck-widget_selected .velocity-brick-chip')
                                || editableEl.querySelector('.ck-widget.ck-widget_selected .velocity-brick-chip');
                            const handled = handleSelectedBrickEnterRef.current?.({
                                token: selectedBrick.getAttribute('token') || '',
                                brickId: selectedBrick.getAttribute('brickId') || '',
                                anchorElement: chip || null
                            });

                            if (handled) {
                                event.preventDefault?.();
                                event.stopPropagation?.();
                                return;
                            }
                        }
                    }

                    const key = event.key;
                    const keyCode = event.keyCode || event.which;
                    const isSlash = key === '/' || key === 'Slash' || keyCode === 191;

                    if (isSlash && !event.metaKey && !event.ctrlKey && !event.altKey) {
                        const selection = editor.model?.document?.selection;
                        const position = selection?.getFirstPosition?.();
                        if (!position) return;

                        capturePendingSlashCommandRef.current?.({
                            root: position.root,
                            path: [...position.path]
                        });
                        return;
                    }

                    handleInsertPanelKeyDownRef.current?.(event);
                };

                editableDoubleClickHandler = (event) => {
                    if (!enableBricksRef.current) return;

                    const chip = event.target?.closest?.('.velocity-brick-chip');
                    if (!chip) return;

                    const brickId = chip.getAttribute('data-velocity-brick-id') || '';
                    const token = chip.getAttribute('data-velocity-token') || '';

                    event.preventDefault?.();
                    event.stopPropagation?.();

                    handleChipDoubleClickRef.current?.({
                        token,
                        brickId,
                        anchorElement: chip
                    });
                };

                editableClickHandler = (event) => {
                    if (!enableBricksRef.current) return;

                    const handle = event.target?.closest?.('.velocity-brick-edit-handle');
                    if (!handle) return;
                    const chip = handle.closest('.velocity-brick-chip');
                    handleEditHandleClickRef.current?.(chip);
                };

                editableEl.addEventListener('keydown', editableKeyDownHandler);
                editableEl.addEventListener('dblclick', editableDoubleClickHandler);
                editableEl.addEventListener('click', editableClickHandler);
            }

            editor.on('velocityBricks:click', (evt, payload) => {
                if (!enableBricksRef.current) return;
                handleVelocityBrickClickRef.current?.(payload);
            });

            let toolbarResolveAttempts = 0;
            const isTitleToolbar = toolBarSet === 'title';
            const resolveToolbarHost = () => {
                const toolbarEl = editor?.ui?.view?.toolbar?.element
                    || editableEl?.closest('.ck-editor')?.querySelector('.ck-toolbar')
                    || editorHostRef.current?.nextElementSibling?.querySelector('.ck-toolbar')
                    || editorHostRef.current?.parentElement?.querySelector('.ck-toolbar');
                if (!enableBricksRef.current || !toolbarEl) return null;

                const itemsHost = toolbarEl.querySelector('.ck-toolbar__items') || toolbarEl;
                const insertHost = ensureBrickToolbarHost(itemsHost, isTitleToolbar);
                if (insertHost) {
                    setToolbarHostNodeRef.current?.(insertHost);
                }
                return insertHost;
            };

            const scheduleToolbarResolve = () => {
                if (toolbarResolveTimer || !enableBricksRef.current) return;
                toolbarResolveAttempts = 0;
                const attempt = () => {
                    toolbarResolveAttempts += 1;
                    const result = resolveToolbarHost();
                    if (result || toolbarResolveAttempts > 15) {
                        toolbarResolveTimer = null;
                        return;
                    }
                    toolbarResolveTimer = setTimeout(attempt, 200);
                };
                attempt();
            };

            scheduleToolbarResolve();
            const toolbarRoot = editor?.ui?.view?.toolbar?.element
                || editableEl?.closest('.ck-editor')
                || editorHostRef.current?.nextElementSibling
                || editorHostRef.current?.parentElement?.querySelector('.ck-editor');
            toolbarObserver = toolbarRoot ? new MutationObserver(() => resolveToolbarHost()) : null;
            if (toolbarObserver && toolbarRoot) {
                toolbarObserver.observe(toolbarRoot, {childList: true, subtree: true});
            }

            editorRef.current = editor;
        };

        const editorConfig = {
            removePlugins: ['FileUpload', 'EmailPreview', 'Outdent', 'Indent'],
            link: {
                defaultProtocol: 'https://'
            },
            fontColor: {
                documentColors: 0,
            },
            heading: getHeadingConfig(toolBarSet),
            typing: {
                transformations: {
                    remove: ['nbsp']
                }
            },
            toolbar: {
                items: toolbarItems,
                shouldNotGroupWhenFull: getShouldNotGroupWhenFull(toolBarSet)
            },
            velocityBricks: {
                matchers: bricks.map(brick => (token) => brick.matchToken?.(token) || null),
                showHandle: showBrickHandle
            }
        };

        ClassicEditor
            .create(editorHostRef.current, editorConfig)
            .then((editor) => handleEditorReady(editor))
            .catch(err => console.error(err))
            .finally(() => {
                setLoading(false);
            });

        document.addEventListener('cx:update-richtext-externally', setDataExternally);
        return () => {
            document.removeEventListener('cx:update-richtext-externally', setDataExternally);
            if (toolbarResolveTimer) {
                clearTimeout(toolbarResolveTimer);
            }
            toolbarObserver?.disconnect();
            const editableEl = editableElementRef.current;
            if (editableEl && editableKeyDownHandler) {
                editableEl.removeEventListener('keydown', editableKeyDownHandler);
            }
            if (editableEl && editableDoubleClickHandler) {
                editableEl.removeEventListener('dblclick', editableDoubleClickHandler);
            }
            if (editableEl && editableClickHandler) {
                editableEl.removeEventListener('click', editableClickHandler);
            }

            if (editorRef.current) {
                managedToolbarCommandNames.forEach((commandName) => {
                    const command = editorRef.current.commands.get(commandName);
                    command?.clearForceDisabled?.('velocity-brick-selection');
                });
                editorRef.current.destroy().catch(console.error);
                editorRef.current = null;
            }
        };
    }, []);

    const getSelectedBrick = () => {
        const selected = editorRef.current?.model?.document?.selection?.getSelectedElement?.();
        if (!selected || selected.name !== 'velocityBrick') return null;
        return {
            token: selected.getAttribute('token') || '',
            label: selected.getAttribute('label') || '',
            brickId: selected.getAttribute('brickId') || ''
        };
    };

    const getSelectedBrickElement = () => {
        const selected = editorRef.current?.model?.document?.selection?.getSelectedElement?.();
        if (!selected || selected.name !== 'velocityBrick') return null;
        return selected;
    };

    const resolveSelectedBrickChipAnchor = () => {
        const editableElement = editorRef.current?.ui?.getEditableElement?.();
        if (!editableElement) return null;

        return editableElement.querySelector('.ck-widget_selected .velocity-brick-chip')
            || editableElement.querySelector('.ck-widget.ck-widget_selected .velocity-brick-chip')
            || null;
    };

    const resolveBrickChipAnchor = (editingToken) => {
        if (!editingToken) return null;
        const editableElement = editorRef.current?.ui?.getEditableElement?.();
        if (!editableElement) return null;

        const chips = Array.from(editableElement.querySelectorAll('.velocity-brick-chip'));
        return chips.find((chip) => chip.getAttribute('data-velocity-token') === editingToken) || null;
    };

    const insertBrick = ({brick, token, label, insertSource, slashCommand}) => {
        const editor = editorRef.current;
        if (!editor || !brick || !token) return;

        const shouldReplaceSlashToken = insertSource === 'slash' && slashCommand;
        if (shouldReplaceSlashToken) {
            const selection = editor.model.document.selection;
            const end = selection?.getFirstPosition?.();

            if (end && end.root === slashCommand.root && comparePaths(end.path, slashCommand.path) >= 0) {
                try {
                    editor.model.change((writer) => {
                        const start = writer.createPositionFromPath(slashCommand.root, slashCommand.path);
                        const range = writer.createRange(start, end);
                        writer.remove(range);
                        const insertAt = writer.createPositionFromPath(slashCommand.root, slashCommand.path);
                        const element = writer.createElement('velocityBrick', {
                            token,
                            label,
                            brickId: brick.id
                        });
                        writer.insert(element, insertAt);
                        writer.setSelection(element, 'on');
                    });
                    return;
                } catch (error) {
                }
            }
        }

        editor.execute('velocityBrick', {
            token,
            label,
            brickId: brick.id
        });
    };

    const updateSelectedBrick = ({brick, token, label, selectedElement}) => {
        const editor = editorRef.current;
        const selected = selectedElement || editor?.model?.document?.selection?.getSelectedElement?.();
        if (!editor || !selected || selected.name !== 'velocityBrick' || !token) return;

        editor.model.change(writer => {
            writer.setAttribute('token', token, selected);
            writer.setAttribute('label', label, selected);
            writer.setAttribute('brickId', brick.id, selected);
        });
    };

    const removeSelectedBrick = (selectedElement) => {
        const editor = editorRef.current;
        const selected = selectedElement || editor?.model?.document?.selection?.getSelectedElement?.();
        if (!editor || !selected || selected.name !== 'velocityBrick') return;

        editor.model.change(writer => {
            writer.remove(selected);
        });
    };

    return {
        editorRef,
        editableElementRef,
        getEditor: () => editorRef.current,
        getSelectedBrick,
        getSelectedBrickElement,
        insertBrick,
        loading,
        removeSelectedBrick,
        resolveBrickChipAnchor,
        resolveSelectedBrickChipAnchor,
        updateSelectedBrick
    };
}
